/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  TranslateContentButton,
  TranslatedCMLViewer,
  TranslatedPlainText,
  TranslationContextProvider,
  TranslationFailedNotification,
} from '@coursera-web/ondemand-translation';
import moment from 'moment';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import mapProps from 'js/lib/mapProps';
import { isSuperuser } from 'js/lib/user';

import { Typography, Typography2, breakpoints, withTheme } from '@coursera/cds-core';

import { getCourseRoleString, isTopContributor } from 'bundles/classmates/utils/lib';
import type { CmlContent } from 'bundles/cml';
import { getFluxiblePostId } from 'bundles/discussions/actions/ThreadDetailsActions';
import discussionsForumsHOC from 'bundles/discussions/components/discussionsForumsHOC';
import AdminDetails from 'bundles/discussions/components/forumsV2-ForumThreads/__components/AdminDetails';
import LikeButtonWithData from 'bundles/discussions/components/forumsV2-LikeButtonWithData';
import PillTag from 'bundles/discussions/components/forumsV2-PillTag';
import { naptimeForumTypes } from 'bundles/discussions/constants';
import type { Contributor } from 'bundles/discussions/lib/types';
import generateReplyData from 'bundles/discussions/utils/generateReplyData';
import { formatReplyLegendId, formatThreadLegendId } from 'bundles/discussions/utils/threadUtils';

import _t from 'i18n!nls/discussions';

import FollowButton from '../../../forumsV2-FollowButton';
import ProfileAvatar from '../../../forumsV2-ProfileAvatar';
import ReplyButton from '../../../forumsV2-ReplyButton';
import { ShimmerParagraph, ShimmerSentence, shimmerColor } from '../../../forumsV2-ShimmerLib';
import type { ForumPostWithCreator } from '../../__providers__/ForumPostDataProvider/__types__';
import ModerationDropdown from '../ModerationDropdown';
import ReplyCMLEdit from '../ReplyCMLEdit';
import ForumsConnectedReplyCMLInput from '../ReplyCMLInput';
import type { Forum } from '../ReplyCMLInput/__types__';
import SoftDelete from '../SoftDelete';

const styles = {
  container: css`
    position: relative;
    padding: 0;
  `,

  title: css`
    margin-bottom: 24px;
    color: var(--cds-color-grey-900);
    font-size: 20px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 24px;

    ${breakpoints.down('xs')} {
      margin-bottom: 16px;
    }
  `,

  content: css`
    display: flex;
    line-height: 36px;
    margin: 0 0 10px 0;
    position: relative;
    align-items: center;
    width: 100%;

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
  `,

  profile: css`
    display: inline-flex;
    align-items: center;
  `,

  ContributorNameWithIcon: css`
    display: flex;
    align-items: center;
  `,

  tags: css`
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
    height: fit-content;
  `,

  postAge: css`
    display: inline-block;
    position: absolute;
    right: 0;
    line-height: 36px;
    vertical-align: middle;

    ${breakpoints.down('xs')} {
      position: static;
      margin-top: 4px;
    }
  `,

  cml: css`
    margin: 0;
    font-size: 14px;

    ${breakpoints.down('sm')} {
      font-size: 13px;
    }
  `,

  responseControls: css`
    font-size: 12px;
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  `,

  controlContainer: css`
    display: inline-block;
    padding: 0 10px 0 0;
  `,

  childContainer: css`
    padding: 0;
  `,

  shimmerWrapper: css`
    width: 100%;
    padding: 16px;

    ${breakpoints.down('sm')} {
      padding: 12px;
    }
  `,

  translateButton: css`
    margin-left: var(--cds-spacing-100);

    ${breakpoints.down('xs')} {
      margin-left: 0;
    }
  `,

  translationFailNotification: css`
    width: fit-content;

    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,

  actionWrapper: css`
    display: inline-flex;
    gap: 8px;
    align-items: center;

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,

  contributorName: css`
    height: 36px;
    display: inline-flex;
    line-height: 36px;
    padding: 0 0 0 var(--cds-spacing-100);

    ${breakpoints.down('xs')} {
      line-height: normal;
      align-items: center;
    }
  `,

  helperStatus: css`
    display: inline-block;
    padding: 0 0 0 var(--cds-spacing-100);
    align-self: center;
  `,
  replyShimmer: css`
    background-color: ${shimmerColor};
    display: block;
    height: 10px;
    width: 40px;
  `,
};

export function ReplyCardShimmer() {
  return (
    <div css={styles.shimmerWrapper}>
      <div css={styles.content}>
        <ShimmerSentence width="100px" />
        <span css={styles.replyShimmer} />
      </div>
      <ShimmerParagraph />
    </div>
  );
}

export function PostAge({ createdAt }: { createdAt: number }) {
  return (
    <Typography2 component="p" color="supportText">
      {moment(new Date(createdAt)).fromNow()}
    </Typography2>
  );
}

// TODO: this needs to be fixed for i18n
// only supports Latin alphabet
export function initialsFromName(fullName: string) {
  const fullNameParts = fullName.split(' ').filter((part) => part !== '');
  if (fullNameParts.length === 0) {
    return '--';
  }
  const [firstName, ...rest] = fullNameParts;
  let lastInitial = '';

  for (let i = 0; i < rest.length; i += 1) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    if (rest[i][0].match(/^[a-z,A-Z]/)) {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      lastInitial = rest[i][0];
    }
  }
  // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return `${firstName[0]}${lastInitial}`;
}

export function ContributorNameWithIcon({
  fullName,
  photoUrl,
  helperStatus,
}: {
  fullName: string | undefined;
  photoUrl?: string;
  helperStatus?: string;
}) {
  const initials = (fullName && initialsFromName(fullName)) || '--';
  return (
    <div css={styles.ContributorNameWithIcon}>
      <ProfileAvatar initials={initials} photoUrl={photoUrl} name={fullName} />
      <Typography2 component="span" variant="subtitleMedium" css={styles.contributorName}>
        {fullName}
      </Typography2>
      <Typography2 component="div" css={styles.helperStatus}>
        {isTopContributor(helperStatus) && <PillTag label={getCourseRoleString(helperStatus)} />}
      </Typography2>
    </div>
  );
}

type State = {
  showReplyCMLInput: boolean;
  shouldFocusReplyEditor: boolean;
};

type PropsFromStore = {
  isHighlighted?: boolean;
  showUndoDelete: boolean;
  replyPostShowAdminDetails?: boolean;
  question: ForumPostWithCreator;
  retryCMLSFromStore:
    | Record<
        string,
        {
          content: CmlContent;
        }
      >
    | undefined;
  savingStateFromStore: Record<string, string> | undefined;
};

type PropsFromCaller = {
  replyPost?: ForumPostWithCreator;
  title?: string;
  creator?: Contributor;
  tags?: JSX.Element;
  cmlContent: CmlContent;
  isUpvoted: boolean;
  isClosed?: boolean;
  upvoteCount: number;
  replyCount?: number;
  createdAt?: number;
  forumQuestionId: string;
  courseId?: string;
  deepLink?: string;
  isRootCard?: boolean;
  dataTestId?: string;
  onDeleteSuccess?: () => void;
};

type ContextId = {
  contextId: string;
};

type PropsToComponent = PropsFromCaller & PropsFromStore & ContextId;

type GetErrorState = (activePost: string) => ErrorState;

type ErrorState = {
  savingState?: string;
  defaultReplyValue?: string;
  forceShowReplyInput: boolean;
};

export class ForumItemCardComponent extends React.LegacyComponentWithChildren<PropsToComponent, State> {
  private node: HTMLDivElement | null = null;

  static contextTypes = {
    currentForumUrl: PropTypes.string,
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof ForumItemCardComponent.contextTypes>;

  state = {
    showReplyCMLInput: false,
    shouldFocusReplyEditor: false,
  };

  componentDidMount() {
    if (this.node && this.isDeepLinked()) {
      this.node.scrollIntoView(true);
    }
  }

  isDeepLinked() {
    const { replyPost } = this.props;

    const {
      router: {
        params: { answer_id: answerId, comment_id: commentId },
      },
    } = this.context;

    return (
      (commentId && replyPost?.type === 'comment' && replyPost?.forumCommentId === commentId) ||
      (answerId && !commentId && replyPost?.type === 'answer' && replyPost?.topLevelForumAnswerId === answerId)
    );
  }

  toggleReply = () => {
    const prevState = this.state;
    this.setState(() => ({
      showReplyCMLInput: !prevState.showReplyCMLInput,
      shouldFocusReplyEditor: true,
    }));
  };

  onDeleteSuccess = (reason: $TSFixMe) => {
    if (!reason && !this.props.replyPost) {
      this.context.router.replace(this.context.currentForumUrl);
    }

    this.props.onDeleteSuccess?.();
  };

  onConnectedReplyCMLInputSubmit = () => {
    this.setState(() => ({ showReplyCMLInput: false, shouldFocusReplyEditor: false }));
  };

  /**
   * Reloads can wipe out the whole subtree, so to preserve error states
   * they need to be persisted from fluxible and rehydrated here.
   * These values will return as undefined if they are not in error.
   *
   * @param topLevelForumAnswerId
   */
  getErrorState: GetErrorState = (storeId) => {
    const { replyCount } = this.props;
    const savingState = this.props.savingStateFromStore?.[storeId];
    const defaultReplyValue = this.props.retryCMLSFromStore?.[storeId]?.content.definition.value;

    const forceShowReplyInput = !!(savingState && defaultReplyValue) && replyCount !== undefined;

    return { savingState, defaultReplyValue, forceShowReplyInput };
  };

  render() {
    const {
      title,
      cmlContent,
      creator,
      forumQuestionId,
      replyPost,
      isHighlighted,
      showUndoDelete,
      contextId,
      replyPostShowAdminDetails,
      question,
      isClosed,
      replyCount,
      dataTestId,
    } = this.props;
    const showReplyCMLEdit = replyPost ? replyPost.showEditor : !!question?.showEditor;

    if (replyPost?.hide) {
      return null;
    }

    const activePost = replyPost || question;
    if (!activePost) {
      return null;
    }

    const replyLegendId = formatReplyLegendId(activePost);

    let errorStates: ErrorState = { savingState: undefined, defaultReplyValue: undefined, forceShowReplyInput: false };
    const options = generateReplyData(activePost, contextId, cmlContent, undefined, undefined);
    const storeId = getFluxiblePostId(options, activePost);
    errorStates = this.getErrorState(storeId);

    return (
      <TranslationContextProvider>
        <div
          ref={(r) => {
            this.node = r;
          }}
          data-testid={dataTestId ?? 'forums-v2-item-card'}
          css={styles.container}
        >
          {title && (
            <Typography variant="h2semibold" css={styles.title}>
              <TranslatedPlainText enableTranslation plainText={title} />
            </Typography>
          )}
          <div css={styles.content}>
            <span css={styles.profile}>
              {creator && (
                <ContributorNameWithIcon
                  fullName={creator?.fullName}
                  photoUrl={creator?.photoUrl}
                  helperStatus={creator?.helperStatus}
                />
              )}
              <span css={styles.tags}>{this.props.tags}</span>
            </span>
            <span data-testid="forums-post-age" css={styles.postAge}>
              {this.props.createdAt && <PostAge createdAt={this.props.createdAt} />}
            </span>
          </div>

          {!showReplyCMLEdit && (
            <div data-testid="forums-translated-cml-viewer" css={styles.cml}>
              <TranslatedCMLViewer cml={cmlContent} isCdsEnabled={true} enableTranslation />
            </div>
          )}
          <TranslationFailedNotification css={styles.translationFailNotification} />

          <div
            /** actionWrapper style has to be conditional because of the way reply input is positioned it messes up with the layout */
            css={[
              styles.responseControls,
              !this.state.showReplyCMLInput && !errorStates.forceShowReplyInput && styles.actionWrapper,
            ]}
          >
            <div css={styles.responseControls}>
              {!showReplyCMLEdit && (
                <span css={styles.controlContainer}>
                  <LikeButtonWithData
                    ariaDescribedBy="like-button"
                    upvotes={this.props.upvoteCount}
                    isUpvoted={this.props.isUpvoted}
                    forumPostId={replyPost?.answerId ? replyPost.answerId : forumQuestionId}
                    creator={this.props.creator}
                  />
                </span>
              )}
              {!showReplyCMLEdit && this.props.creator && replyCount !== undefined && !isClosed && (
                <span css={styles.controlContainer}>
                  <ReplyButton
                    forumQuestionId={forumQuestionId}
                    replies={replyCount}
                    creator={this.props.creator}
                    onClick={() => this.toggleReply()}
                    ariaLabel={_t('Reply #{count} to post #{title}', {
                      title,
                      count: replyCount || '',
                    })}
                  />
                </span>
              )}
              {!showReplyCMLEdit && !replyPost && <FollowButton question={question} />}
              <TranslateContentButton css={styles.translateButton} />
              {!showReplyCMLEdit && (
                <ModerationDropdown
                  onDeleteSuccess={this.onDeleteSuccess}
                  creator={creator}
                  post={activePost}
                  forumLink={this.context.currentForumUrl}
                  ariaDescribedBy={replyLegendId}
                  isHighlighted={replyPost ? isHighlighted : undefined}
                  showingAdminDetails={replyPost ? replyPostShowAdminDetails : question.showAdminDetails}
                />
              )}
              {(replyPost || question) && (
                <AdminDetails
                  post={replyPost ? { ...replyPost, showAdminDetails: !!replyPostShowAdminDetails } : question}
                />
              )}
            </div>
          </div>

          {showReplyCMLEdit && <ReplyCMLEdit reply={activePost} />}
          {(this.state.showReplyCMLInput || errorStates.forceShowReplyInput) && (
            <ForumsConnectedReplyCMLInput
              question={question}
              parentPost={activePost}
              savingState={errorStates.savingState}
              shouldFocusReplyEditor={this.state.shouldFocusReplyEditor}
              ariaLabel={_t('Reply to this thread')}
              ariaDescribedBy={formatThreadLegendId(forumQuestionId)}
              defaultValue={errorStates.defaultReplyValue}
              contextId={contextId}
              onSubmit={this.onConnectedReplyCMLInputSubmit}
            />
          )}

          {replyPost?.state?.deleted && (
            <SoftDelete entry={replyPost} showUndoDelete={showUndoDelete} isForQuestion={false} />
          )}

          {this.props.children && <div css={styles.childContainer}>{this.props.children}</div>}
        </div>
      </TranslationContextProvider>
    );
  }
}

type InputProps = {
  currentForum: Forum;
  courseId: string;
  parentPost: ForumPostWithCreator;
};

export default compose<PropsToComponent, React.PropsWithChildren<PropsFromCaller>>(
  connectToStores<PropsFromCaller & PropsFromStore, PropsFromCaller>(
    ['ThreadDetailsStore', 'CourseMembershipStore'],
    ({ ThreadDetailsStore, CourseMembershipStore }, { replyPost, forumQuestionId }) => {
      return {
        question: ThreadDetailsStore.getQuestion(forumQuestionId),
        answerLoadedState: ThreadDetailsStore.getAnswerLoadedState(forumQuestionId),
        isHighlighted: replyPost ? ThreadDetailsStore.isHighlighted(replyPost) : undefined,
        showUndoDelete: CourseMembershipStore.getCourseRole() !== 'LEARNER' || isSuperuser(),
        replyPostShowAdminDetails: replyPost
          ? ThreadDetailsStore.findPosts(replyPost, true)[0]?.showAdminDetails
          : undefined,
        savingStateFromStore: ThreadDetailsStore.savingStates,
        retryCMLSFromStore: ThreadDetailsStore.retryCml,
      };
    }
  ),
  withTheme,
  discussionsForumsHOC({ fields: ['link', 'title'] }),
  mapProps((props: InputProps) => {
    const addedProps: ContextId = {
      contextId: props.courseId,
    };
    if (props.currentForum && props.currentForum.forumType.typeName === naptimeForumTypes.groupForumType) {
      addedProps.contextId = props.currentForum.forumType.definition.groupId;
    }
    return addedProps;
  })
)(ForumItemCardComponent);
